import { IFileData, IOption } from '@vtbhome/juicyfront/types';
import { UseHandlersParameters } from './types';
import { UploadedFile } from '@shared/types/input-file';
import { InputFileSearchValue } from '@shared/ui/input-file-search/types';

export const useHandlers = ({
    max,
    value,
    filteredFileData,
    inputTypeFields,
    onChange,
    setIsShowSearchPlaceholder,
    setInputTypeFields,
}: UseHandlersParameters) => {
    const handleChange = (value: InputFileSearchValue) => {
        if (onChange) {
            onChange(value);
        }
    };

    const handleChangeInputType = (e: React.ChangeEvent<HTMLElement>) => {
        const option = e.target as HTMLInputElement | null;
        if (!option) {
            return;
        }
        const optionValue = option.value;
        const newFields = inputTypeFields.map((field) => {
            if (field.value === optionValue) {
                return { ...field, checked: true };
            }
            return { ...field, checked: false };
        });
        setInputTypeFields(newFields);
    };

    /* Upload input handlers */

    const handleAddLocalFiles = async (files: IFileData[]) => {
        const uploadedCount = value.initialUploadedFiles.length;
        const deletedCount = value.deletedUploadedFiles.length;
        const localCount = value.localFiles.length + files.length;

        let newLocalFiles = [...value.localFiles, ...files];
        const newFilesQueue = [...value.filesQueue, ...files];

        if (max) {
            const exceed = uploadedCount + localCount - deletedCount - max;
            newLocalFiles = exceed > 0 ? newLocalFiles.slice(0, -exceed) : newLocalFiles;
        }

        handleChange({ ...value, localFiles: newLocalFiles, filesQueue: newFilesQueue });
    };

    /* Search input handlers */

    const handleAddUploadedFile = (options: IOption[]) => {
        const optionFileId = options[0]?.value;
        const file = filteredFileData.find((fileItem) => String(fileItem.id) === optionFileId);
        if (!file) {
            return;
        }
        const uploadedFile: UploadedFile = {
            id: file.id,
            url: file.url,
            name: file.name,
            extension: file.meta.extension,
            size: file.meta.size,
        };
        const newValue = { ...value };
        newValue.addedUploadedFiles = [...newValue.addedUploadedFiles, uploadedFile];
        newValue.filesQueue = [...newValue.filesQueue, uploadedFile];
        setIsShowSearchPlaceholder(false);
        handleChange(newValue);
    };

    /* File list handlers */

    const handleRemoveInitialUploadedFile = (file: UploadedFile) => () => {
        const newValue = { ...value };
        const isDeleted = newValue.deletedUploadedFiles.some((deletedFile) => deletedFile.id === file.id);
        if (!isDeleted) {
            newValue.deletedUploadedFiles.push(file);
            newValue.filesQueue = newValue.filesQueue.filter((queueFile) => queueFile.id !== file.id);
        }
        handleChange(newValue);
    };

    const handleRemoveAddedUploadedFile = (file: UploadedFile) => () => {
        const newValue = { ...value };
        newValue.addedUploadedFiles = value.addedUploadedFiles.filter(
            (addedUploadedFile) => addedUploadedFile.url !== file.url
        );
        newValue.filesQueue = newValue.filesQueue.filter((queueFile) => queueFile.id !== file.id);
        handleChange(newValue);
    };

    const handleRemoveLocalFile = (file: IFileData) => () => {
        const newValue = { ...value };
        newValue.localFiles = value.localFiles.filter((localFile) => localFile.file !== file.file);
        newValue.filesQueue = newValue.filesQueue.filter((queueFile) => {
            if ('file' in queueFile) {
                return queueFile.file ? queueFile.file !== file.file : true;
            }
            return true;
        });
        handleChange(newValue);
    };

    return {
        handleChangeInputType,
        handleAddLocalFiles,
        handleAddUploadedFile,
        handleRemoveInitialUploadedFile,
        handleRemoveAddedUploadedFile,
        handleRemoveLocalFile,
    };
};
